import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronDown, Check, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API } from '../../config/api';

const BusinessRegisterModal = ({ isOpen, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showCountryCodeDropdown, setShowCountryCodeDropdown] = useState(false);
  const [showBusinessStructureDropdown, setShowBusinessStructureDropdown] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showStateDropdown, setShowStateDropdown] = useState(false);

  // Form fields
  const [businessName, setBusinessName] = useState('');
  const [addressData, setAddressData] = useState({
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    country: '',
    zipcode: ''
  });
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [businessCategory, setBusinessCategory] = useState('');
  const [businessStructure, setBusinessStructure] = useState('');
  const [errors, setErrors] = useState({});

  const BUSINESS_STRUCTURES = [
    {display_name: 'Individual / Sole Proprietor', value: 'individual'},
    {display_name: 'Company (LLC, Corp, Partnership)', value: 'company'},
    {display_name: 'Non-Profit Organization', value: 'non_profit'},
    {display_name: 'Government Entity', value: 'government_entity'}
  ];

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
      fetchCountries();
    }
  }, [isOpen]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(API.BUSINESS_CATEGORIES);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      setCategories([
        {display_name: 'Property Management', value: 'property_management'},
        {display_name: 'Retail', value: 'retail'},
        {display_name: 'Services', value: 'services'},
        {display_name: 'Restaurant', value: 'restaurant'},
        {display_name: 'Professional Services', value: 'professional_services'}
      ]);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axios.get(API.COUNTRIES);
      const data = response.data.countries;
      setCountries(data);
      // If only one country, auto-select it
      if (data.length === 1) {
        setAddressData(prev => ({ ...prev, country: data[0].name }));
        setCountryCode(data[0].phone_code);
        fetchStates(data[0].country_id);
      }
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    }
  };

  const fetchStates = async (countryId) => {
    try {
      const response = await axios.get(`${API.STATES}?country_id=${countryId}`);
      setStates(response.data.states);
    } catch (error) {
      console.error('Failed to fetch states:', error);
      setStates([]);
    }
  };

  const handleCountrySelect = (country) => {
    setAddressData(prev => ({ ...prev, country: country.name, state: '' }));
    setCountryCode(country.phone_code);
    setShowCountryDropdown(false);
    setStates([]);
    fetchStates(country.country_id);
  };

  const handleStateSelect = (state) => {
    setAddressData(prev => ({ ...prev, state: state.name }));
    setShowStateDropdown(false);
  };

  const getFormattedAddress = () => {
    if (!addressData.addressLine1) return '';
    const parts = [
      addressData.addressLine1,
      addressData.addressLine2,
      addressData.city,
      addressData.state,
      addressData.zipcode,
      addressData.country
    ].filter(Boolean);
    return parts.join(', ');
  };

  const validate = () => {
    const newErrors = {};
    if (!businessName.trim()) newErrors.businessName = 'Business name is required';
    if (!addressData.addressLine1) newErrors.address = 'Business address is required';
    if (!contactName.trim()) newErrors.contactName = 'Contact person name is required';
    if (!email || !/\S+@\S+\.\S+/.test(email)) newErrors.email = 'Valid email is required';
    if (!phoneNumber) newErrors.phone = 'Phone number is required';
    if (!businessCategory) newErrors.category = 'Please select a business category';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    try {
      const payload = {
        name: businessName,
        addressLine1: addressData.addressLine1,
        addressLine2: addressData.addressLine2,
        city: addressData.city,
        state: addressData.state,
        zipcode: addressData.zipcode,
        country: addressData.country,
        contactName,
        contactEmailAddress: email,
        countryCode,
        contactPhoneNumber: phoneNumber,
        category: businessCategory?.value || businessCategory,
        business_structure: businessStructure
      };

      await axios.post(API.BUSINESS_REGISTER, payload);
      setShowSuccess(true);
    } catch (err) {
      console.error('Registration error:', err);
      const errorMessage = err.response?.data?.detail || 'Registration failed. Please try again.';
      setErrors({ submit: typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage) });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setShowSuccess(false);
    setBusinessName('');
    setAddressData({ addressLine1: '', addressLine2: '', city: '', state: '', country: '', zipcode: '' });
    setContactName('');
    setEmail('');
    setCountryCode('');
    setPhoneNumber('');
    setBusinessCategory('');
    setBusinessStructure('');
    setStates([]);
    setErrors({});
    onClose();
  };

  const saveAddress = () => {
    if (!addressData.addressLine1 || !addressData.city || !addressData.state || !addressData.zipcode) {
      return;
    }
    setShowAddressModal(false);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-dark-100 rounded-2xl border border-white/10"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors z-10"
          >
            <X size={24} />
          </button>

          {showSuccess ? (
            // Success State
            <div className="p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-accent flex items-center justify-center">
                <Check size={40} className="text-dark" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Registration Successful!</h3>
              <p className="text-gray-400 mb-6">
                Thank you for registering your business. Our team will reach out to you within 48 hours.
              </p>
              <button
                onClick={handleClose}
                className="w-full py-3 bg-gradient-to-r from-accent to-accent-dark text-dark font-semibold rounded-xl"
              >
                Done
              </button>
            </div>
          ) : (
            // Form
            <form onSubmit={handleSubmit} className="p-6 md:p-8">
              <h2 className="text-2xl font-bold text-white mb-2">Register Your Business</h2>
              <p className="text-gray-400 mb-6">Fill in your business details below</p>

              {errors.submit && (
                <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
                  {errors.submit}
                </div>
              )}

              {/* Business Name */}
              <div className="mb-4">
                <label className="block text-sm text-gray-300 mb-2">Business Name *</label>
                <input
                  type="text"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="Enter your business name"
                  className={`w-full px-4 py-3 bg-dark-200 border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-accent transition-colors ${
                    errors.businessName ? 'border-red-500' : 'border-white/10'
                  }`}
                />
                {errors.businessName && <p className="mt-1 text-sm text-red-400">{errors.businessName}</p>}
              </div>

              {/* Business Address */}
              <div className="mb-4">
                <label className="block text-sm text-gray-300 mb-2">Business Address *</label>
                <button
                  type="button"
                  onClick={() => setShowAddressModal(true)}
                  className={`w-full px-4 py-3 bg-dark-200 border rounded-xl text-left transition-colors ${
                    errors.address ? 'border-red-500' : 'border-white/10'
                  }`}
                >
                  <span className={getFormattedAddress() ? 'text-white' : 'text-gray-500'}>
                    {getFormattedAddress() || 'Tap to enter address'}
                  </span>
                </button>
                {errors.address && <p className="mt-1 text-sm text-red-400">{errors.address}</p>}
              </div>

              {/* Contact Name */}
              <div className="mb-4">
                <label className="block text-sm text-gray-300 mb-2">Contact Person Name *</label>
                <input
                  type="text"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder="Enter contact person name"
                  className={`w-full px-4 py-3 bg-dark-200 border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-accent transition-colors ${
                    errors.contactName ? 'border-red-500' : 'border-white/10'
                  }`}
                />
                {errors.contactName && <p className="mt-1 text-sm text-red-400">{errors.contactName}</p>}
              </div>

              {/* Email */}
              <div className="mb-4">
                <label className="block text-sm text-gray-300 mb-2">Email Address *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter email address"
                  className={`w-full px-4 py-3 bg-dark-200 border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-accent transition-colors ${
                    errors.email ? 'border-red-500' : 'border-white/10'
                  }`}
                />
                {errors.email && <p className="mt-1 text-sm text-red-400">{errors.email}</p>}
              </div>

              {/* Phone */}
              <div className="mb-4">
                <label className="block text-sm text-gray-300 mb-2">Phone Number *</label>
                <div className="flex gap-3">
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowCountryCodeDropdown(!showCountryCodeDropdown)}
                      className="flex items-center gap-2 px-4 py-3 bg-dark-200 border border-white/10 rounded-xl text-white min-w-[110px]"
                    >
                      {countryCode ? `${countries.find(c => c.phone_code === countryCode)?.name?.slice(0, 2) || ''} (${countryCode})` : 'Code'}
                      <ChevronDown size={16} />
                    </button>
                    {showCountryCodeDropdown && (
                      <div className="absolute top-full left-0 mt-1 min-w-[200px] bg-dark-200 border border-white/10 rounded-xl overflow-hidden z-10">
                        {countries.map((c) => (
                          <button
                            key={c.country_id}
                            type="button"
                            onClick={() => {
                              setCountryCode(c.phone_code);
                              setShowCountryCodeDropdown(false);
                            }}
                            className={`w-full px-4 py-2 text-left transition-colors ${
                              countryCode === c.phone_code ? 'bg-accent/20 text-accent' : 'text-white hover:bg-dark-300'
                            }`}
                          >
                            {c.name} ({c.phone_code})
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="Phone number"
                    className={`flex-1 px-4 py-3 bg-dark-200 border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-accent transition-colors ${
                      errors.phone ? 'border-red-500' : 'border-white/10'
                    }`}
                  />
                </div>
                {errors.phone && <p className="mt-1 text-sm text-red-400">{errors.phone}</p>}
              </div>

              {/* Category */}
              <div className="mb-4">
                <label className="block text-sm text-gray-300 mb-2">Business Category *</label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                    className={`w-full flex items-center justify-between px-4 py-3 bg-dark-200 border rounded-xl transition-colors ${
                      errors.category ? 'border-red-500' : 'border-white/10'
                    }`}
                  >
                    <span className={businessCategory ? 'text-white' : 'text-gray-500'}>
                      {businessCategory?.display_name || businessCategory || 'Select a category'}
                    </span>
                    <ChevronDown size={16} className="text-gray-400" />
                  </button>
                  {showCategoryDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-dark-200 border border-white/10 rounded-xl z-10">
                      {categories.map((cat) => (
                        <button
                          key={cat.value || cat}
                          type="button"
                          onClick={() => {
                            setBusinessCategory(cat);
                            setShowCategoryDropdown(false);
                          }}
                          className={`w-full px-4 py-3 text-left transition-colors flex items-center justify-between ${
                            businessCategory?.value === cat.value ? 'bg-accent/20 text-accent' : 'text-white hover:bg-dark-300'
                          }`}
                        >
                          {cat.display_name || cat}
                          {businessCategory?.value === cat.value && <Check size={16} />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {errors.category && <p className="mt-1 text-sm text-red-400">{errors.category}</p>}
              </div>

              {/* Business Structure */}
              <div className="mb-6">
                <label className="block text-sm text-gray-300 mb-2">Business Structure</label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowBusinessStructureDropdown(!showBusinessStructureDropdown)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-dark-200 border border-white/10 rounded-xl transition-colors"
                  >
                    <span className={businessStructure ? 'text-white' : 'text-gray-500'}>
                      {BUSINESS_STRUCTURES.find(s => s.value === businessStructure)?.display_name || 'Select a structure'}
                    </span>
                    <ChevronDown size={16} className="text-gray-400" />
                  </button>
                  {showBusinessStructureDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-dark-200 border border-white/10 rounded-xl z-10">
                      {BUSINESS_STRUCTURES.map((structure) => (
                        <button
                          key={structure.value}
                          type="button"
                          onClick={() => {
                            setBusinessStructure(structure.value);
                            setShowBusinessStructureDropdown(false);
                          }}
                          className={`w-full px-4 py-3 text-left transition-colors flex items-center justify-between ${
                            businessStructure === structure.value ? 'bg-accent/20 text-accent' : 'text-white hover:bg-dark-300'
                          }`}
                        >
                          {structure.display_name}
                          {businessStructure === structure.value && <Check size={16} />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3.5 bg-gradient-to-r from-accent to-accent-dark text-dark font-semibold rounded-xl hover:shadow-lg hover:shadow-accent/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    Registering...
                  </>
                ) : (
                  'Register Business'
                )}
              </button>
            </form>
          )}

          {/* Address Modal */}
          <AnimatePresence>
            {showAddressModal && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-dark-100 rounded-2xl p-6 md:p-8 overflow-y-auto"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-white">Enter Address</h3>
                  <button
                    type="button"
                    onClick={() => setShowAddressModal(false)}
                    className="p-2 text-gray-400 hover:text-white"
                  >
                    <X size={24} />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">Address Line 1 *</label>
                    <input
                      type="text"
                      value={addressData.addressLine1}
                      onChange={(e) => setAddressData({ ...addressData, addressLine1: e.target.value })}
                      placeholder="Street address"
                      className="w-full px-4 py-3 bg-dark-200 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-accent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-300 mb-2">Address Line 2</label>
                    <input
                      type="text"
                      value={addressData.addressLine2}
                      onChange={(e) => setAddressData({ ...addressData, addressLine2: e.target.value })}
                      placeholder="Apt, suite, unit, etc. (optional)"
                      className="w-full px-4 py-3 bg-dark-200 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-accent"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-300 mb-2">City *</label>
                      <input
                        type="text"
                        value={addressData.city}
                        onChange={(e) => setAddressData({ ...addressData, city: e.target.value })}
                        placeholder="City"
                        className="w-full px-4 py-3 bg-dark-200 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-accent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-300 mb-2">State *</label>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => { if (states.length > 0) setShowStateDropdown(!showStateDropdown); }}
                          className={`w-full flex items-center justify-between px-4 py-3 bg-dark-200 border border-white/10 rounded-xl transition-colors ${
                            states.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          disabled={states.length === 0}
                        >
                          <span className={addressData.state ? 'text-white text-sm' : 'text-gray-500 text-sm'}>
                            {addressData.state || (states.length === 0 ? 'Select country first' : 'Select state')}
                          </span>
                          <ChevronDown size={14} className="text-gray-400" />
                        </button>
                        {showStateDropdown && (
                          <div className="absolute top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-dark-200 border border-white/10 rounded-xl z-20">
                            {states.map((s) => (
                              <button
                                key={s.state_id}
                                type="button"
                                onClick={() => handleStateSelect(s)}
                                className={`w-full px-4 py-2 text-left text-sm transition-colors ${
                                  addressData.state === s.name ? 'bg-accent/20 text-accent' : 'text-white hover:bg-dark-300'
                                }`}
                              >
                                {s.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-300 mb-2">Country</label>
                      {countries.length <= 1 ? (
                        <div className="px-4 py-3 bg-dark-300 border border-white/10 rounded-xl text-gray-400">
                          {addressData.country || 'Loading...'}
                        </div>
                      ) : (
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                            className="w-full flex items-center justify-between px-4 py-3 bg-dark-200 border border-white/10 rounded-xl transition-colors"
                          >
                            <span className={addressData.country ? 'text-white text-sm' : 'text-gray-500 text-sm'}>
                              {addressData.country || 'Select country'}
                            </span>
                            <ChevronDown size={14} className="text-gray-400" />
                          </button>
                          {showCountryDropdown && (
                            <div className="absolute top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-dark-200 border border-white/10 rounded-xl z-20">
                              {countries.map((c) => (
                                <button
                                  key={c.country_id}
                                  type="button"
                                  onClick={() => handleCountrySelect(c)}
                                  className={`w-full px-4 py-2 text-left text-sm transition-colors ${
                                    addressData.country === c.name ? 'bg-accent/20 text-accent' : 'text-white hover:bg-dark-300'
                                  }`}
                                >
                                  {c.name}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm text-gray-300 mb-2">Zipcode *</label>
                      <input
                        type="text"
                        value={addressData.zipcode}
                        onChange={(e) => setAddressData({ ...addressData, zipcode: e.target.value })}
                        placeholder="Zipcode"
                        className="w-full px-4 py-3 bg-dark-200 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-accent"
                      />
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={saveAddress}
                  className="w-full mt-6 py-3.5 bg-gradient-to-r from-accent to-accent-dark text-dark font-semibold rounded-xl"
                >
                  Save Address
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BusinessRegisterModal;
